import { Injectable } from '@angular/core';


@Injectable({
  providedIn: 'root'
})
export class RequirementsService {

  constructor() { }

  private readonly MIN_WIDTH = 1366;
  private readonly MIN_HEIGHT = 768;

  isChrome(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isChromeLike = /Chrome|CriOS|Chromium/.test(ua);
    const isExcluded = /Edg|Edge|OPR|Opera|Brave|SamsungBrowser|Silk/.test(ua);
    return isChromeLike && !isExcluded;
  }

  isNotebookScreen(minWidth = 1024, minHeight = 640): boolean {
    if (typeof window === 'undefined') {
      return false; // server-side rendering or unavailable window
    }

    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';

    // 1. Exclude explicit mobile/tablet User Agents
    const isMobileOrTablet = /Mobile|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    if (isMobileOrTablet) {
      console.log('isNotebookScreen: Rejected due to Mobile/Tablet UA', ua);
      return false;
    }

    const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(platform) || /Macintosh/.test(ua);
    const hasTouch = navigator.maxTouchPoints > 0 || (navigator as any).msMaxTouchPoints > 0;

    if (isMac && hasTouch) {
      console.log('isNotebookScreen: Rejected due to iPad masquerading as Mac', { platform, maxTouchPoints: navigator.maxTouchPoints });
      return false;
    }

  
    const screenWidth = window.screen ? window.screen.width : 0;
    const screenHeight = window.screen ? window.screen.height : 0;

    console.log('Screen dimensions check:', {
      screenWidth,
      screenHeight,
      minWidth,
      minHeight
    });

    return screenWidth >= minWidth && screenHeight >= minHeight;
  }

  /**
   * Convenience method: checks if we're running Chrome on a notebook-like screen.
   */
  isChromeOnNotebook(minWidth = 1024, minHeight = 640): boolean {
    return this.checkRequirements(minWidth, minHeight).valid;
  }

  /**
   * Checks all requirements and returns the result with a specific reason code if failed.
   */
getMinimumScreen(): { width: number; height: number } {
  return { 
    width: this.MIN_WIDTH || 1366, 
    height: this.MIN_HEIGHT || 768 
  };
}

  checkRequirements(minWidth = 1024, minHeight = 640): { valid: boolean; reason?: 'MOBILE_UA' | 'TABLET_UA' | 'IPAD_DESKTOP' | 'SCREEN_SIZE' | 'BROWSER_NOT_CHROME' } {
    if (typeof window === 'undefined') {
      return { valid: false, reason: 'BROWSER_NOT_CHROME' }; // SSR
    }

    // 1. Check Browser (Must be Chrome-like)
    if (!this.isChrome()) {
      return { valid: false, reason: 'BROWSER_NOT_CHROME' };
    }

    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';

    // 2. Exclude explicit mobile/tablet User Agents
    const isMobileOrTablet = /Mobile|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    if (isMobileOrTablet) {
      console.log('checkRequirements: Rejected due to Mobile/Tablet UA', ua);
      // Try to distinguish tablet vs mobile if possible, but generic is fine for now.
      // If "iPad" or "Tablet" is in UA, return TABLET_UA, else MOBILE_UA
      if (/iPad|Tablet/i.test(ua)) {
        return { valid: false, reason: 'TABLET_UA' };
      }
      return { valid: false, reason: 'MOBILE_UA' };
    }

    // 3. Detect iPad on iOS 13+ (Macintosh UA + Touch Points)
    const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(platform) || /Macintosh/.test(ua);
    const hasTouch = navigator.maxTouchPoints > 0 || (navigator as any).msMaxTouchPoints > 0;

    if (isMac && hasTouch) {
      console.log('checkRequirements: Rejected due to iPad masquerading as Mac', { platform, maxTouchPoints: navigator.maxTouchPoints });
      return { valid: false, reason: 'IPAD_DESKTOP' };
    }

    // 4. Check Screen Size (Device Capability)
    const screenWidth = window.screen ? window.screen.width : 0;
    const screenHeight = window.screen ? window.screen.height : 0;

    if (screenWidth < minWidth || screenHeight < minHeight) {
      console.log('checkRequirements: Rejected due to Screen Size', { screenWidth, screenHeight, minWidth, minHeight });
      return { valid: false, reason: 'SCREEN_SIZE' };
    }

    return { valid: true };
  }
}
